import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { Badge } from "../../ui/badge";
import { Sparkles } from "lucide-react";
import { Progress } from "../../ui/progress";
const DecisionOptimization = ({ data }) => {
    const decisionOptimization = data.decision_optimization;
    const contributions = decisionOptimization?.process_contributions ?? [];
    const percentAutomated = decisionOptimization?.decision_optimization_percent ?? 0;
    const overallAutomationPotential = data.efficiency_parameters?.overall_automation_potential ?? 0;
    const automationBasedFormula = (overallAutomationPotential / 100) * 30;
    const displayedPercent = automationBasedFormula || percentAutomated;
    const renderContribution = (process) => {
        return (<div key={process.process} className="space-y-2 rounded-lg border border-muted/50 bg-card/80 p-4 shadow-sm">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium">{process.process}</span>
          <span className="text-sm text-muted-foreground">
            {process.weighted_points.toFixed(2)} weighted points
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Progress value={process.automation_percentage} className="flex-1"/>
          <span className="text-xs text-muted-foreground min-w-[80px]">
            {process.automation_percentage}% auto
          </span>
        </div>
        <div className="text-xs text-muted-foreground">
          {process.ftes} FTEs × {process.automation_percentage}% = {process.weighted_points.toFixed(2)}
        </div>
      </div>);
    };
    return (<Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles className="h-5 w-5 text-accent"/>
            <div>
              <CardTitle>Decision Optimization Potential</CardTitle>
              <CardDescription>
                AI can automate approximately {displayedPercent.toFixed(1)}% of procurement decision points
              </CardDescription>
            </div>
          </div>
          <Badge variant="secondary" className="text-lg px-4 py-1">
            {displayedPercent.toFixed(1)}% Decision Points Automated
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div className="bg-primary/5 p-4 rounded-lg border border-primary/25 shadow-sm">
            <h4 className="font-semibold mb-2">Calculation Formula (Reference)</h4>
            <div className="font-mono text-sm bg-muted/50 p-3 rounded">
              Decision Points Automated = (Overall Automation Potential / 100) × 30
            </div>
            <div className="font-mono text-sm bg-muted/50 p-3 rounded mt-2">
              = ({overallAutomationPotential.toFixed(1)} / 100) x 30 ~= {automationBasedFormula.toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Dashboard-reported potential: {percentAutomated.toFixed(1)}% decision points automated.
            </p>
          </div>

          <div>
            <h4 className="font-semibold mb-3">Process-Level Weighted Contributions</h4>
            <div className="space-y-4">
              {contributions.length > 0 ? (contributions.map(renderContribution)) : (<p className="text-sm text-muted-foreground">No process contributions available.</p>)}
            </div>
          </div>

          <Card className="bg-muted/50 border border-muted/70 text-muted-foreground">
            <CardHeader>
              <CardTitle className="text-lg">Interpretation</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                AI can automate approximately {displayedPercent.toFixed(1)}% of procurement decision points, 
                freeing strategic resources to focus on high-value judgment calls, supplier relationships, 
                and innovation initiatives.
              </p>

              <div>
                <h4 className="font-semibold text-sm mb-2">Key Benefits:</h4>
                <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                  <li>Faster, more consistent decisions across procurement processes</li>
                  <li>Reduced cognitive load on procurement teams</li>
                  <li>Better compliance with policies and sourcing guidelines</li>
                  <li>Data-driven insights at critical decision points</li>
                  <li>Focus on strategic activities requiring human judgment</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </CardContent>
    </Card>);
};
export default DecisionOptimization;
